import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, Save, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { SuccessCoachingWeekEditor } from '../components/SuccessCoachingWeekEditor';
import { SuccessCoachingUnsuccessfulEditor } from '../components/SuccessCoachingUnsuccessfulEditor';
import { SuccessCoachingWelcomeEditor } from '../components/SuccessCoachingWelcomeEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_ROLES = ['system_admin', 'executive', 'operations_manager'];

const BuiltInRules = ({ program }) => (
  <Card className="border-dashed" data-testid="sc-built-in-rules">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2"><Lock className="w-4 h-4" />Built into the system</CardTitle>
    </CardHeader>
    <CardContent className="text-sm text-muted-foreground space-y-1">
      <p>• Note subject that starts this flow: <span className="font-medium text-foreground">{program.note_subject}</span></p>
      <p>• Week is picked from the latest Weekly Caseload Review Success Coaching Session counter (0 sessions = Week 1 … 11 = Week 12). At 12 or more sessions the weekly flow stops.</p>
      <p>• {program.required_answers} of the week's questions must be answered; the rest are optional.</p>
      <p>• Win-Wall question every week: "{program.win_wall_question}"</p>
      <p>• AI summary: {program.summary_instructions}</p>
      <p>• Merge fields: {'{{success_coaching_week_topic}}'}, {'{{win_wall}}'}, {'{{personalized_encounter_summary}}'}</p>
    </CardContent>
  </Card>
);

export default function SuccessCoachingProgramPage() {
  const { user } = useAuth();
  const canEdit = ADMIN_ROLES.includes(user?.role);
  const [program, setProgram] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [unsuccessful, setUnsuccessful] = useState({});
  const [welcome, setWelcome] = useState({});
  const [mergeFields, setMergeFields] = useState([]);
  const [messageMergeFields, setMessageMergeFields] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, mf, t] = await Promise.all([
          axios.get(`${API}/settings/success-coaching-program`),
          axios.get(`${API}/admin/custom-merge-fields`).catch(() => ({ data: [] })),
          axios.get(`${API}/message-templates/merge-fields`).catch(() => ({ data: [] })),
        ]);
        setProgram(p.data);
        setWeeks(p.data.weeks);
        setUnsuccessful(p.data.unsuccessful || {});
        setWelcome(p.data.welcome || {});
        setMergeFields([...(mf.data || [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })));
        setMessageMergeFields(Array.isArray(t.data) ? t.data : (t.data.merge_fields || []));
      } catch (e) {
        toast.error('Failed to load the Success Coaching program');
      }
    };
    load();
  }, []);

  const save = async (quiet = false) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/success-coaching-program`, { weeks, unsuccessful, welcome });
      setProgram(res.data);
      setWeeks(res.data.weeks);
      setUnsuccessful(res.data.unsuccessful || {});
      setWelcome(res.data.welcome || {});
      if (!quiet) toast.success('Success Coaching program saved');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!program) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="success-coaching-program-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/settings?tab=snap-et" className="text-sm text-muted-foreground hover:underline flex items-center gap-1 mb-2" data-testid="sc-back-link">
            <ArrowLeft className="w-4 h-4" />Back to SNAP E&T Settings
          </Link>
          <h1 className="text-2xl font-bold">Success Coaching Weekly Program</h1>
          <p className="text-muted-foreground">Set each week's topic, questions, and the email and text sent after a Success Coaching Encounter note. Merge fields such as {'{{participant_first_name}}'} and {'{{success_coaching_week_topic}}'} are filled in when sent.</p>
        </div>
        {canEdit && (
          <Button onClick={() => save()} disabled={saving} data-testid="sc-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save Program
          </Button>
        )}
      </div>
      <BuiltInRules program={program} />
      <SuccessCoachingWelcomeEditor value={welcome} mergeFields={messageMergeFields} readOnly={!canEdit} onChange={setWelcome} onSave={() => save(true)} />
      <div className="space-y-4">
        {weeks.map((w, i) => (
          <SuccessCoachingWeekEditor key={w.week_number} week={w} readOnly={!canEdit}
            mergeFields={mergeFields}
            messageMergeFields={messageMergeFields}
            onChange={(updated) => setWeeks(prev => prev.map((x, idx) => idx === i ? updated : x))} />
        ))}
      </div>
      <SuccessCoachingUnsuccessfulEditor value={unsuccessful} mergeFields={messageMergeFields} readOnly={!canEdit} onChange={setUnsuccessful} />
      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={() => save()} disabled={saving} data-testid="sc-save-btn-bottom">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save Program
          </Button>
        </div>
      )}
    </div>
  );
}
